import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type ExtraCrumb = string | string[] | null;

interface BreadcrumbContextValue {
  extraCrumb: ExtraCrumb;
  setExtraCrumb: (value: ExtraCrumb) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export const BreadcrumbProvider = ({ children }: { children: ReactNode }) => {
  const [extraCrumb, setExtraCrumb] = useState<ExtraCrumb>(null);
  const value = useMemo(() => ({ extraCrumb, setExtraCrumb }), [extraCrumb]);

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

export const useBreadcrumbExtra = (): string[] => {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) throw new Error('useBreadcrumbExtra must be used within a BreadcrumbProvider');
  if (!ctx.extraCrumb) return [];
  return Array.isArray(ctx.extraCrumb) ? ctx.extraCrumb : [ctx.extraCrumb];
};

export const useSetBreadcrumbExtra = (label: ExtraCrumb) => {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) throw new Error('useSetBreadcrumbExtra must be used within a BreadcrumbProvider');
  const { setExtraCrumb } = ctx;
  const key = Array.isArray(label) ? label.join('|') : label;

  useEffect(() => {
    setExtraCrumb(label);
    return () => setExtraCrumb(null);
  }, [key, setExtraCrumb]);
};
